import { FormEvent, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";

interface LoginFormProps {
  showSignupLink?: boolean;
  heading?: string;
  subheading?: string;
}

export function LoginForm({
  showSignupLink = true,
  heading,
  subheading
}: LoginFormProps) {
  const navigate = useNavigate();
  const { login, loading } = useAuthStore();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const headingText = heading ?? t("auth.signIn", "Sign in");
  const subheadingText = subheading ?? t("auth.enterCredentials", "Enter your credentials to access the system.");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      // Trim to defend against mobile keyboards adding leading/trailing
      // whitespace (autocomplete) — a common cause of "invalid credentials".
      await login(email.trim(), password.trim());
      navigate("/supervisor");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.loginFailed", "Login failed"));
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white/95 p-6 shadow-lg">
      <div className="mb-4 flex items-center gap-3">
        <img src="/g-data-logo-no-bg.png" alt="G-Data" className="h-10 w-10 object-contain" />
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500">G-Data Labs</p>
          <p className="text-sm font-semibold text-gray-900">{t("brand.subtitle", "Security Patrol")}</p>
        </div>
      </div>
      <h2 className="text-xl font-semibold text-gray-900">{headingText}</h2>
      <p className="mt-1 text-sm text-gray-600">{subheadingText}</p>

      <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
        <label className="block text-sm">
          <span className="text-gray-700">{t("auth.email", "Email")}</span>
          <input
            type="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">{t("auth.password", "Password")}</span>
          <input
            type="password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="current-password"
            className="mt-1 w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          className="w-full rounded-md py-2 brand-green-gradient transition-opacity"
          disabled={loading}
        >
          {loading ? t("auth.signingIn", "Signing in…") : t("auth.signIn", "Sign in")}
        </button>
      </form>

      {showSignupLink && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            {t("auth.needAccess", "Need access to the security system?")}{" "}
            <Link to="/signup" className="font-medium brand-text-green hover:underline">
              {t("auth.applyToJoin", "Apply to join our team")}
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}
